import ActionButton from 'components/buttons/action-button/ActionButton';
import { withModal } from 'components/modal/ModalHOC';
import StatusLabel from 'components/status-label/StatusLabel';
import {
  EntertainmentVideoStatus,
  FullEntertainmentVideo
} from 'modules/entertainment-videos/types';
import moment from 'moment';
import React, { useContext } from 'react';
import { GalleryModalItem, SubcampaignInfluencerProfile } from 'types';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { __t, applyTranslations } from 'utils/language';
import { EntertainmentVideoReview } from '../entertainment-video-review';
import { ENTERTAINMENT_VIDEO_STATUS_TO_STATUS_LABEL_TYPE } from '../../constants';
import './entertainment-video-review-card.scss';
import DeliverableContentContext from 'modules/deliverables/components/DeliverableContentContext';
import { dispatch } from 'redux-store/dispatch';
import { setIsEditMode } from 'modules/entertainment-videos/reducers';

applyTranslations(require('./entertainment-video-review-card.i18n.json'));

function translate(part: string) {
  return __t(`entertainmentVideoReviewCard${part}`);
}

const ENTERTAINMENT_VIDEO_REVIEW_STATUSES = [
  EntertainmentVideoStatus.DECLINED,
  EntertainmentVideoStatus.PENDING,
  EntertainmentVideoStatus.PUBLISHING,
  EntertainmentVideoStatus.PUBLISH_FAILED,
  EntertainmentVideoStatus.PUBLISHED,
  EntertainmentVideoStatus.UNPUBLISHING,
  EntertainmentVideoStatus.UNPUBLISH_FAILED,
  EntertainmentVideoStatus.UNPUBLISHED
];

const ContainerWithModal = withModal(
  <div className="ev-review-card__media-container" />,
  WITH_GALLERY_MODAL_CONFIG
);

const ReviewButton = withModal(ActionButton, {
  modalContent: EntertainmentVideoReview,
  closeIconPosition: 'corner',
  closeIconType: 'fancy',
  onModalClosed: () => {
    dispatch(setIsEditMode(false));
  }
});

export function EntertainmentVideoReviewCard({
  deliverableId,
  offerId,
  fullEntertainmentVideo,
  isForBrand = false,
  influencerProfile
}: {
  deliverableId: number,
  offerId: number,
  fullEntertainmentVideo: FullEntertainmentVideo,
  isForBrand?: boolean,
  influencerProfile: SubcampaignInfluencerProfile
}) {
  const pageType = useContext(DeliverableContentContext);

  const { id, status, name, createdAt, reviewedAt } =
    fullEntertainmentVideo.entertainmentVideo;
  const itemCount = fullEntertainmentVideo.items.length;

  const isReviewStatus = ENTERTAINMENT_VIDEO_REVIEW_STATUSES.includes(status);

  function getGalleryItems() {
    if (!fullEntertainmentVideo.media) {
      return [];
    }

    const items: Array<GalleryModalItem> = [
      {
        type: 'collabary-video',
        url: fullEntertainmentVideo.media.url,
        assetUrl: fullEntertainmentVideo.media.url
      }
    ];

    if (fullEntertainmentVideo.media?.thumbnailUrl) {
      // Display thumbnail as first item, hence `.unshift()`
      items.unshift({
        type: 'collabary-image',
        url: fullEntertainmentVideo.media.thumbnailUrl,
        assetUrl: fullEntertainmentVideo.media.thumbnailUrl,
        thumbnailUrl: fullEntertainmentVideo.media.thumbnailUrl
      });
    }

    return items;
  }

  return (
    <div className="ev-review-card">
      <ContainerWithModal
        key={`entertainment-media-attachment-${id}`}
        modalContentProps={{
          items: getGalleryItems(),
          currentPosition: 0
        }}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
        <MediaPreview fullEntertainmentVideo={fullEntertainmentVideo} />
      </ContainerWithModal>
      <div className="ev-review-card__content">
        <StatusLabel
          size="small"
          label={translate(`.labels.status.${status}`)}
          type={ENTERTAINMENT_VIDEO_STATUS_TO_STATUS_LABEL_TYPE[status]}
        />
        <ul className="ev-feature-list ev-review-card__feature-list">
          <li className="ev-feature-list__item">
            <div className="ev-feature-list__label">
              {translate('.featureList.name')}:
            </div>
            <div className="ev-feature-list__value">{name}</div>
          </li>
          <li className="ev-feature-list__item">
            <div className="ev-feature-list__label">
              {translate('.featureList.items')}:
            </div>
            <div className="ev-feature-list__value">{itemCount}</div>
          </li>
          <li className="ev-feature-list__item">
            <div className="ev-feature-list__label">
              {translate('.featureList.createdAt')}:
            </div>
            <div className="ev-feature-list__value">
              {moment(createdAt).format('DD/MM/YYYY')}
            </div>
          </li>
          <li className="ev-feature-list__item">
            <div className="ev-feature-list__label">
              {translate('.featureList.reviewedAt')}:
            </div>
            <div className="ev-feature-list__value">
              {typeof reviewedAt === 'string'
                ? moment(reviewedAt).format('DD/MM/YYYY')
                : '–'}
            </div>
          </li>
        </ul>
      </div>
      {isReviewStatus && isForBrand && (
        <footer className="ev-review-card__footer">
          <ReviewButton
            width="maximum"
            style="small"
            color="primary"
            modalContentProps={{
              pageType,
              deliverableId,
              offerId,
              influencerProfile,
              initialEntertainmentVideo: fullEntertainmentVideo
            }}
          >
            <span style={{ fontWeight: 'bold' }}>
              {translate('.labels.review')}
            </span>
            &nbsp;{translate('.labels.entertainmentVideo')}
          </ReviewButton>
        </footer>
      )}
    </div>
  );
}

function MediaPreview({
  fullEntertainmentVideo
}: {
  fullEntertainmentVideo: FullEntertainmentVideo
}) {
  if (!fullEntertainmentVideo.media) {
    return null;
  }

  if (typeof fullEntertainmentVideo.media.thumbnailUrl === 'string') {
    return (
      <div
        style={{
          height: '100%',
          width: '100%',
          backgroundImage: `url(${fullEntertainmentVideo.media.thumbnailUrl})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}
      />
    );
  }

  return (
    <p className="ev-review-card__media-text">{translate('.noThumbnail')}</p>
  );
}
